import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { formatCurrency } from "@/types/crm";
import { findOrCreateCustomer, createDealFromInquiry } from "@/utils/customerSync";
import { logAudit } from "@/utils/auditLog";

export default function LeadCapture() {
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get("vehicle_id");
  const source = searchParams.get("source") || "website";

  const [vehicle, setVehicle] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: ""
  });

  useEffect(() => {
    if (vehicleId) {
      loadVehicle();
    }
  }, [vehicleId]);

  const loadVehicle = async () => {
    const { data, error } = await supabase
      .from("vehicles")
      .select("id, year, make, model, price, vin, vehicle_images(image_url, is_primary)")
      .eq("id", vehicleId)
      .single();

    if (!error && data) {
      setVehicle(data);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!formData.firstName || !formData.lastName || !formData.phone) {
        toast.error("Please fill in all required fields.");
        setIsLoading(false);
        return;
      }

      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      const submissionMessage = vehicleId 
        ? `Lead Capture Form Submission (Source: ${source}).\n${formData.message}`
        : `Lead Capture Form Submission (Source: ${source}).\n${formData.message}`;

      const { error: submitError } = await supabase.rpc("submit_contact_form", {
          p_name: fullName,
          p_email: formData.email,
          p_phone: formData.phone,
          p_vehicle_id: vehicleId || null,
          p_message: submissionMessage,
      });

      if (submitError) throw submitError;

      // Log lead capture to audit log
      await logAudit({
        entityType: 'lead',
        action: 'captured',
        metadata: {
          lead_name: fullName,
          email: formData.email,
          phone: formData.phone,
          vehicle_id: vehicleId || null,
          source: source,
        },
      });

      // Also create/update customer in CRM and create deal if vehicle-linked
      try {
        const customerId = await findOrCreateCustomer({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email || undefined,
          phone: formData.phone,
        });
        if (customerId && vehicleId && vehicle) {
          const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
          await createDealFromInquiry(customerId, vehicleId, vehicleName);
        }
      } catch (crmError) {
        console.error('CRM sync error (non-blocking):', crmError);
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error(error);
      toast.error("An error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-lg border-primary/20">
          <CardContent className="pt-10 pb-10 text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
            <h2 className="text-2xl font-bold text-foreground">Request Received</h2>
            <p className="text-muted-foreground">
              Thank you for your interest! Our team has received your information and will be in touch with you shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight">Express Interest</h1>
          <p className="mt-2 text-muted-foreground">
            Provide your details and we'll get right back to you.
          </p>
        </div>

        {vehicle && (
          <Card className="overflow-hidden border-primary/20 shadow-sm bg-card/50 backdrop-blur-sm">
            <div className="flex sm:flex-row flex-col">
              <div className="w-full sm:w-1/3 aspect-video sm:aspect-square bg-muted relative">
                {(() => {
                  const primaryImg = (vehicle as any).vehicle_images?.find((img: any) => img.is_primary)?.image_url 
                    || (vehicle as any).vehicle_images?.[0]?.image_url;
                  return primaryImg ? (
                    <img src={primaryImg} alt="Vehicle" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No Image</div>
                  );
                })()}
              </div>
              <div className="p-5 flex-1 flex flex-col justify-center">
                <div className="text-sm font-medium text-primary mb-1">Vehicle of Interest</div>
                <h3 className="font-bold text-lg leading-tight">{vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}</h3>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xl font-bold">{formatCurrency(vehicle.price)}</span>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">VIN: {vehicle.vin}</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card className="shadow-lg border-t-4 border-t-primary">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">First Name <span className="text-red-500">*</span></label>
                  <Input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" required />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Last Name <span className="text-red-500">*</span></label>
                  <Input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" required />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Phone Number <span className="text-red-500">*</span></label>
                <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" required />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Email Address</label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Message (Optional)</label>
                <Textarea 
                  name="message" 
                  value={formData.message} 
                  onChange={handleChange} 
                  placeholder="I'm interested in coming to see this vehicle..."
                  rows={4}
                />
              </div>

              <Button type="submit" className="w-full py-6 text-lg mt-6" disabled={isLoading}>
                {isLoading ? "Submitting..." : "Submit Inquiry"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
